import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Car, Search, Plus, Settings, CarFront } from 'lucide-react';

import Pagination from 'components/common/Pagination';
import VehiclesTable from 'components/common/VehiclesTable';
import VehicleModal from 'components/modals/vehicle/VehicleModal';
import { getVehicles, getVehiclesOverview } from 'actions/vehicleActions';
import VehicleModel from 'models/VehicleModel';
import ColumnsPicker from 'components/common/ColumnsPicker';
import TableColumn, { TableColumOrderStatus } from 'models/TableColumnModel';
import User from 'models/UserModel';

import actionTypes from 'helpers/actionTypes';
import placeholders from 'helpers/placeholders';
import config from 'helpers/config';
import axios from 'helpers/axios';
import { FaCar } from 'react-icons/fa';

interface ComponentState {
    errorMessage: string | null;
    searchPhrase: string;
    page: number;
    itemsPerPage: number;
    orderBy?: string;
    isDescending?: boolean;
    vehiclesCount: number;
    isCreateVehicleModalOpen: boolean;
    isEditVehicleModalOpen: boolean;
    editableVehicleId: string | null;
    tableColumns: TableColumn[];
    [x: string]: any;
}

interface Props {
    vehicles: VehicleModel[];
    vehiclesLoading: boolean;
    vehiclesCount: number;
    currentUser: User;
    dispatch: Dispatch;
}

class VehiclesListPage extends React.Component<Props, ComponentState> {
    constructor(props: Props) {
        super(props);
        this.getVehicles = this.getVehicles.bind(this);
        this.pageChanged = this.pageChanged.bind(this);
        this.handleSearch = this.handleSearch.bind(this);

        this.state = {
            page: 1,
            orderBy: '',
            isDescending: false,
            itemsPerPage: 15,
            vehiclesCount: 0,
            searchPhrase: '',
            errorMessage: null,
            editableVehicleId: null,
            isCreateVehicleModalOpen: false,
            isEditVehicleModalOpen: false,
            tableColumns: vehicleListColumns,
        };
    }

    componentDidMount() {
        const { dispatch } = this.props;
        dispatch<any>(getVehiclesOverview());
        this.getVehicles();
    }

    getVehicles() {
        const { dispatch } = this.props;
        const { page, itemsPerPage, searchPhrase, orderBy, isDescending } = this.state;
        dispatch<any>(getVehicles({ page, itemsPerPage, searchPhrase, orderBy, isDescending }));
    }

    handleSearch(event: React.FormEvent<HTMLInputElement>) {
        const { vehiclesCount } = this.props;
        const searchPhrase = event.currentTarget.value;
        if (searchPhrase?.length > 1) {
            this.setState(
                {
                    searchPhrase,
                    page: 1,
                    itemsPerPage: vehiclesCount,
                },
                this.getVehicles,
            );
        } else {
            this.setState(
                {
                    searchPhrase: '',
                    page: 1,
                    itemsPerPage: 15,
                },
                () => this.componentDidMount(),
            );
        }
    }

    pageChanged(page?: number) {
        if (page) {
            this.setState({ page }, this.getVehicles);
        }
    }

    editVehicle(vehicleId: string) {
        this.setState({ isEditVehicleModalOpen: true, editableVehicleId: vehicleId });
    }

    modalClosed(vehicleCreated?: boolean, vehicleUpdated?: boolean) {
        this.setState({ isCreateVehicleModalOpen: false, isEditVehicleModalOpen: false, editableVehicleId: null });
        if (vehicleCreated || vehicleUpdated) {
            this.componentDidMount();
        }
    }

    getVehicleModal() {
        const { isCreateVehicleModalOpen, isEditVehicleModalOpen, editableVehicleId } = this.state;
        if (isEditVehicleModalOpen && !!editableVehicleId) {
            return (
                <VehicleModal
                    vehicleId={editableVehicleId}
                    onClose={(vehicleCreated?: boolean, vehicleEdited?: boolean) =>
                        this.modalClosed(vehicleCreated, vehicleEdited)
                    }
                ></VehicleModal>
            );
        } else if (isCreateVehicleModalOpen) {
            return (
                <VehicleModal
                    onClose={(vehicleCreated?: boolean, vehicleEdited?: boolean) =>
                        this.modalClosed(vehicleCreated, vehicleEdited)
                    }
                ></VehicleModal>
            );
        }
    }

    order(columnName: string) {
        const column = vehicleListColumns.find((tableColumn) => tableColumn.name === columnName);

        if (column?.orderStatus === TableColumOrderStatus.None) {
            column.orderStatus = TableColumOrderStatus.Ascending;
        } else if (column?.orderStatus === TableColumOrderStatus.Ascending) {
            column.orderStatus = TableColumOrderStatus.Descending;
        } else if (column?.orderStatus === TableColumOrderStatus.Descending) {
            column.orderStatus = TableColumOrderStatus.Ascending;
        }

        const orderByString = `${column?.name}.${
            column?.orderStatus === TableColumOrderStatus.Ascending ? 'DESC' : 'ASC'
        }`;

        vehicleListColumns.forEach((col) => {
            if (col !== column) {
                col.orderStatus = TableColumOrderStatus.None;
            }
        });
        this.setState(
            { orderBy: orderByString, isDescending: column?.orderStatus === TableColumOrderStatus.Descending },
            () => this.getVehicles(),
        );
    }

    updateVisibleColumns(updatedColumns: TableColumn[]) {
        const { userSetting } = this.props.currentUser;
        userSetting.vehicleListColumns = JSON.stringify(updatedColumns);
        const { dispatch } = this.props;
        dispatch({ type: actionTypes.UPDATE_USER_SETTINGS_SUCCESS, payload: userSetting });
        axios.put(`${config.apiUrl}/user/settings`, userSetting);
        this.setState({ tableColumns: updatedColumns });
    }
    render() {
        const { page, itemsPerPage, isCreateVehicleModalOpen, isEditVehicleModalOpen, tableColumns } = this.state;
        const { vehicles, vehiclesLoading, vehiclesCount, currentUser } = this.props;

        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                                <FaCar className="h-8 w-8 text-blue-600" />
                                <span>Vehicle List</span>
                            </h2>

                            <p className="text-gray-600 mt-1">Manage your vehicle database and maintenance records.</p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => this.setState({ isCreateVehicleModalOpen: !isCreateVehicleModalOpen })}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                            >
                                <Plus className="h-4 w-4" />
                                <span>Add Vehicle</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-8">
                    {/* Search and Statistics */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div className="flex-1 max-w-md">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                    <input
                                        onChange={this.handleSearch}
                                        name="searchPhrase"
                                        placeholder={placeholders.VEHICLES_SEARCH}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2 text-sm text-gray-600">
                                    <Car className="h-4 w-4" />
                                    <span className="font-semibold">
                                        {vehiclesCount} vehicle{vehiclesCount !== 1 ? 's' : ''} found
                                    </span>
                                </div>

                                <div className="border-l border-gray-300 pl-4">
                                    <ColumnsPicker
                                        onChange={(updatedColumns) => this.updateVisibleColumns(updatedColumns)}
                                        columns={
                                            currentUser?.userSetting?.vehicleListColumns
                                                ? JSON.parse(currentUser.userSetting.vehicleListColumns)
                                                : tableColumns
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Vehicles Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <VehiclesTable
                            vehicles={vehicles}
                            isLoading={vehiclesLoading}
                            onEdit={(vehicleId: string) => this.editVehicle(vehicleId)}
                            onOrder={(column: string) => this.order(column)}
                            columns={
                                currentUser?.userSetting?.vehicleListColumns
                                    ? JSON.parse(currentUser.userSetting.vehicleListColumns)
                                    : tableColumns
                            }
                        />
                    </div>

                    {/* Pagination */}
                    {true && (
                        <div className="mt-8 flex justify-center">
                            <Pagination
                                currentPage={page}
                                pageSize={itemsPerPage}
                                totalItems={vehiclesCount}
                                pageChanged={this.pageChanged}
                            />
                        </div>
                    )}
                </div>

                {/* Modals */}
                {(isCreateVehicleModalOpen || isEditVehicleModalOpen) && this.getVehicleModal()}
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    vehicles: state.vehicle.vehicles,
    vehiclesLoading: state.vehicle.vehiclesLoading,
    vehiclesCount: state.vehicle.vehiclesCount,
    currentUser: state.user.currentUser,
});

export default connect(mapStateToProps)(VehiclesListPage);

const vehicleListColumns: TableColumn[] = [
    {
        name: 'model',
        active: true,
        text: 'Model',
        sortable: true,
        orderStatus: TableColumOrderStatus.None,
    },
    {
        name: 'make',
        active: true,
        text: 'Make',
        sortable: true,
        orderStatus: TableColumOrderStatus.None,
    },
    {
        name: 'series',
        active: true,
        text: 'Trim',
        sortable: true,
        orderStatus: TableColumOrderStatus.None,
    },
    {
        name: 'year',
        active: true,
        text: 'Year',
        sortable: true,
        orderStatus: TableColumOrderStatus.None,
    },
    {
        name: 'VIN',
        active: true,
        text: 'VIN',
        sortable: true,
        orderStatus: TableColumOrderStatus.None,
    },
    {
        name: 'notes',
        active: true,
        text: 'Note',
        sortable: false,
        orderStatus: TableColumOrderStatus.None,
    },
    {
        name: 'customer',
        active: true,
        text: 'Customer name',
        sortable: true,
        orderStatus: TableColumOrderStatus.None,
    },
];
